import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { AuthUser, getStoredUser, login as authLogin, logout as authLogout } from '../services/auth';

interface AppContextProps {
    user: AuthUser;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    loginUser: (email: string, password: string) => Promise<void>;
    logoutUser: () => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser>(null);
    const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);

    useEffect(() => {
        const stored = getStoredUser();
        if (stored) {
            setUser(stored);
        }
        setIsAuthLoading(false);
    }, []);

    const loginUser = async (email: string, password: string) => {
        setIsAuthLoading(true);
        try {
            const loggedIn = await authLogin(email, password);
            setUser(loggedIn);
        } finally {
            setIsAuthLoading(false);
        }
    };

    const logoutUser = () => {
        authLogout();
        setUser(null);
    };

    return (
        <AppContext.Provider 
            value={{ 
                user, 
                isAuthenticated: Boolean(user), 
                isAuthLoading, 
                loginUser, 
                logoutUser 
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useApp = (): AppContextProps => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error('useApp must be used within AppProvider');
    return ctx;
};

export { AppContext, AppProvider };

